import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Activity, AlertTriangle, ArrowRight, ChevronDown, Clock, Cpu, Loader2,
  MessageCircle, Play, Pause, Send, Sparkles, Target, Trash2, Trophy, Users,
  Zap, CheckCircle2, Circle, AlertOctagon, X, Calendar, DoorOpen, DoorClosed,
  Briefcase, Building2,
} from 'lucide-react';
import ExecutionCockpit from './monetize/ExecutionCockpit';
import MissionReviewModal from './monetize/MissionReviewModal';
import MissionToast from './monetize/MissionToast';
import AgentWorkshop from './workshop/AgentWorkshop';
import { AgentPulseBar, LoopKanban, LiveActivityStream } from './hq/LoopOffice';
import ValidationModal from './execution/ValidationModal';
import DriftBanner from './aria/DriftBanner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ACCENT = '#FFD700';

const STATUS_DEFS = {
  backlog:     { label: 'Backlog',     color: 'rgba(15,15,30,0.55)', Icon: Circle },
  in_progress: { label: 'In progress', color: '#06B6D4',                Icon: Loader2 },
  blocked:     { label: 'Blocked',     color: '#FF6B6B',                Icon: AlertOctagon },
  done:        { label: 'Done',        color: '#22C55E',                Icon: CheckCircle2 },
};

const KIND_BADGES = {
  'deck-revision':      { label: 'Deck V+', color: '#FFD700' },
  'persona-feedback':   { label: 'Persona feedback', color: '#06B6D4' },
  'build-improvement':  { label: 'Build', color: '#7C3AED' },
  'outreach':           { label: 'Outreach', color: '#F97316' },
  'sales-call':         { label: 'Sales call', color: '#22C55E' },
  'content':            { label: 'Content', color: '#A855F7' },
  'investor-warmup':    { label: 'Investor', color: '#3B82F6' },
  'research':           { label: 'Research', color: '#94A3B8' },
  'data-analysis':      { label: 'Analysis', color: '#06B6D4' },
};

export default function HQView() {
  const [overview, setOverview] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [mode, setMode] = useState('office');  // 'office' | 'execution' | 'workshop'
  const [workshopAgent, setWorkshopAgent] = useState('builder');
  // When the founder opens a task from Execution (Additional Tasks), the
  // Workshop should land directly on that task. We thread the taskId through
  // HQView state, AgentWorkshop, AgentPane, and TaskRail.
  const [workshopPreselectTaskId, setWorkshopPreselectTaskId] = useState(null);
  const [hq, setHq] = useState(null);
  const [autopilot, setAutopilot] = useState(null); // {state, next_pulse_in_s}
  const [toggling, setToggling] = useState(false);
  // Mission state — tracked globally so the toast & modal work regardless of
  // whether the founder is on the Office or Execution view.
  const [missions, setMissions] = useState([]);
  const [reviewMission, setReviewMission] = useState(null);   // mission currently in modal
  const [launchedToast, setLaunchedToast] = useState(null);   // freshly spawned mission (toast)
  const [readyToast, setReadyToast] = useState(null);         // mission ready for review (toast)
  const seenLaunchedRef = useRef(new Set());
  const seenReadyRef = useRef(new Set());
  const seenDismissedRef = useRef(new Set());
  const pollRef = useRef(null);
  const pulseRef = useRef(null);

  const loadOverview = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/hq/overview`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setOverview(d);
        if (!activeId && d.ventures?.length) setActiveId(d.ventures[0].monetize_id);
      }
    } catch {}
  }, [activeId]);

  const loadHq = useCallback(async (mid, auto = false) => {
    if (!mid) return;
    try {
      const url = `${API}/api/hq/${mid}${auto ? '?auto=true' : ''}`;
      const r = await fetch(url, { credentials: 'include' });
      if (r.ok) setHq(await r.json());
    } catch {}
  }, []);

  const loadAutopilot = useCallback(async (mid) => {
    if (!mid) return;
    try {
      const r = await fetch(`${API}/api/hq/${mid}/autopilot`, { credentials: 'include' });
      if (r.ok) setAutopilot(await r.json());
    } catch {}
  }, []);

  // Mission polling — drives the Office reveal toast + review modal.
  const loadMissions = useCallback(async (mid) => {
    if (!mid) return;
    try {
      const r = await fetch(`${API}/api/hq/${mid}/missions`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      const items = d.missions || [];
      setMissions(items);

      // Detect freshly launched missions (status=brainstorming|executing) we
      // haven't surfaced yet, and the most recent one awaiting approval.
      for (const m of items) {
        if (m.status === 'brainstorming' || m.status === 'executing') {
          if (!seenLaunchedRef.current.has(m.mission_id)) {
            seenLaunchedRef.current.add(m.mission_id);
            setLaunchedToast(m);
          }
        }
        if (m.status === 'awaiting_approval' && !seenDismissedRef.current.has(m.mission_id)) {
          if (!seenReadyRef.current.has(m.mission_id)) {
            seenReadyRef.current.add(m.mission_id);
            setReadyToast(m);
          }
          // Auto-open the review modal — but only once per mission, and
          // only if the founder hasn't already dismissed it.
          if (!reviewMission || reviewMission.mission_id !== m.mission_id) {
            // Open only for the most recent awaiting mission to avoid flicker
            // if multiple are open: take the freshest by created_at.
            const awaiting = items.filter(x => x.status === 'awaiting_approval' && !seenDismissedRef.current.has(x.mission_id));
            const freshest = awaiting.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            if (freshest && (!reviewMission || reviewMission.mission_id !== freshest.mission_id)) {
              setReviewMission(freshest);
            }
          }
        }
      }
    } catch {}
  }, [reviewMission]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => {
    if (!activeId) return;
    loadHq(activeId, false);
    loadAutopilot(activeId);
    loadMissions(activeId);
  }, [activeId, loadHq, loadAutopilot, loadMissions]);

  // Live polling — fast cadence when autopilot ON to surface streaming events
  useEffect(() => {
    if (!activeId) return;
    const interval = autopilot?.state === 'play' ? 2500 : 9000;
    pollRef.current = setInterval(() => {
      loadHq(activeId, false);
      loadAutopilot(activeId);
      loadOverview();
      loadMissions(activeId);
    }, interval);
    return () => clearInterval(pollRef.current);
  }, [activeId, autopilot?.state, loadHq, loadOverview, loadAutopilot, loadMissions]);

  // Faster cadence (2s) when we know a mission is mid-flight — so brainstorm
  // messages and Trello cards animate live in the Office.
  useEffect(() => {
    if (!activeId) return undefined;
    const hasLive = missions.some(m => m.status === 'brainstorming' || m.status === 'executing');
    if (!hasLive) return undefined;
    const t = setInterval(() => loadMissions(activeId), 2000);
    return () => clearInterval(t);
  }, [activeId, missions, loadMissions]);

  // Autopilot pulse loop — fires every 8s if state=play; backend enforces real cooldown
  useEffect(() => {
    if (!activeId) return undefined;
    if (autopilot?.state !== 'play') {
      if (pulseRef.current) clearInterval(pulseRef.current);
      return undefined;
    }
    const fire = async () => {
      try {
        await fetch(`${API}/api/hq/${activeId}/autopilot/pulse`, {
          method: 'POST', credentials: 'include',
        });
        loadHq(activeId, false);
        loadAutopilot(activeId);
      } catch {}
    };
    fire(); // fire immediately on play
    pulseRef.current = setInterval(fire, 8000);
    return () => clearInterval(pulseRef.current);
  }, [activeId, autopilot?.state, loadHq, loadAutopilot]);

  const togglePlay = async () => {
    if (!activeId || toggling) return;
    setToggling(true);
    const next = autopilot?.state === 'play' ? 'pause' : 'play';
    try {
      const r = await fetch(`${API}/api/hq/${activeId}/autopilot`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: next }),
      });
      if (r.ok) setAutopilot(await r.json());
      loadHq(activeId);
    } finally {
      setToggling(false);
    }
  };

  if (!overview) {
    return (
      <div style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12 }}>
        Loading HQ…
      </div>
    );
  }

  if (!overview.ventures.length) {
    return (
      <div style={{ flex: 1, height: '100%', overflowY: 'auto', background: 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)' }}>
        <div style={{ maxWidth: 720, margin: '40px auto 0', padding: 36, textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', width: 64, height: 64,
            background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.3)',
            color: ACCENT, alignItems: 'center', justifyContent: 'center', marginBottom: 16,
          }}>
            <Cpu size={28} />
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px', color: '#0F0F1A' }}>
            HQ Mission Control
          </h1>
          <p style={{ fontSize: 13, color: 'rgba(15,15,30,0.6)', lineHeight: 1.6, maxWidth: 540, margin: '0 auto' }}>
            This is where Hermes orchestrates your entire venture team in real time.
            Promote a project to <strong style={{ color: ACCENT }}>Monetize</strong> first — once you have personas,
            a deck and a business model, HQ becomes alive.
          </p>
        </div>
      </div>
    );
  }

  const activeOverview = overview.ventures.find(v => v.monetize_id === activeId);

  // A venture is "execution-ready" once its traction layer is in (dataroom +
  // personas + business_models). The execution mode dropdown only lists those.
  const executionReadyIds = new Set(
    (overview.ventures || [])
      .filter(v => {
        const m = v.modules || {};
        return (m.dataroom?.status === 'complete')
          && (m.personas?.status === 'complete')
          && (m.business_models?.status === 'complete');
      })
      .map(v => v.monetize_id),
  );
  const canExecute = activeOverview && executionReadyIds.has(activeId);

  return (
    <div data-testid="hq-view" style={{
      flex: 1, height: '100%', display: 'flex', flexDirection: 'column',
      background: mode === 'execution' ? '#0A0A0B' : 'linear-gradient(180deg, #FBFAF7 0%, #F4F3F0 100%)',
      color: '#0F0F1A', overflow: 'hidden',
    }}>
      <ModeSwitcher
        mode={mode} setMode={setMode}
        canExecute={canExecute}
        active={activeOverview}
        overview={overview}
        activeId={activeId}
        setActiveId={setActiveId}
        executionReadyIds={executionReadyIds}
      />
      {mode === 'execution' && canExecute ? (
        <ExecutionCockpit
          monetizeId={activeId}
          onBack={() => setMode('office')}
          onOpenInWorkshop={(agentKey, taskId) => {
            setWorkshopAgent(agentKey);
            setWorkshopPreselectTaskId(taskId);
            setMode('workshop');
          }}
        />
      ) : mode === 'workshop' ? (
        <AgentWorkshop
          hq={hq}
          agentKey={workshopAgent}
          onAgentChange={(k) => { setWorkshopAgent(k); setWorkshopPreselectTaskId(null); }}
          onTaskChanged={() => loadHq(activeId)}
          preselectTaskId={workshopPreselectTaskId}
        />
      ) : (
        <>
          <HQTopBar
            overview={overview}
            activeId={activeId}
            setActiveId={setActiveId}
            active={activeOverview}
            hq={hq}
            autopilot={autopilot}
            toggling={toggling}
            onTogglePlay={togglePlay}
          />
          {!hq ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(15,15,30,0.48)', fontSize: 12 }}>
              <Loader2 size={14} style={{ animation: 'spin 0.7s linear infinite', marginRight: 8 }} /> Loading mission state…
            </div>
          ) : (
            <HQBody hq={hq} autopilot={autopilot}
              onTaskChanged={() => loadHq(activeId)}
              onOpenWorkshop={(agentKey) => { setWorkshopAgent(agentKey); setMode('workshop'); }}
              onSwitchToExecution={() => setMode('execution')}
              onPostMessage={async (msg) => {
              await fetch(`${API}/api/hq/${activeId}/messages`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: msg }),
              });
              loadHq(activeId);
            }} />
          )}
        </>
      )}

      {/* Mission toast — fires when Aria launches a task from the Execution chat. */}
      {launchedToast && (
        <MissionToast
          mission={launchedToast}
          kind="launched"
          onOpenOffice={() => { setMode('office'); setLaunchedToast(null); }}
          onDismiss={() => setLaunchedToast(null)}
        />
      )}
      {/* Ready-for-review toast — fires when a mission reaches awaiting_approval. */}
      {readyToast && !reviewMission && (
        <MissionToast
          mission={readyToast}
          kind="ready"
          onOpenOffice={() => { setReviewMission(readyToast); setReadyToast(null); }}
          onDismiss={() => setReadyToast(null)}
        />
      )}
      {/* The review modal — overlays everything when an artifact is awaiting verdict. */}
      {reviewMission && (
        <MissionReviewModal
          mission={reviewMission}
          onClose={() => {
            // Mark dismissed so we don't re-pop until verdict is recorded
            seenDismissedRef.current.add(reviewMission.mission_id);
            setReviewMission(null);
            setReadyToast(null);
          }}
          onDecided={() => {
            seenDismissedRef.current.add(reviewMission.mission_id);
            setReviewMission(null);
            setReadyToast(null);
            // Founder gave a verdict → drop them in the Execution chat so they
            // see Aria's contextual follow-up (and continue the conversation)
            // instead of being stranded on the Office board.
            if (canExecute) setMode('execution');
            // Re-sync state so the resolved card shows in 'done' lane
            loadHq(activeId);
            loadMissions(activeId);
          }}
        />
      )}
    </div>
  );
}

/* =================== Mode switcher (Office ↔ Execution ↔ Workshop) =================== */
function ModeSwitcher({ mode, setMode, canExecute, active, overview, activeId, setActiveId, executionReadyIds }) {
  const [open, setOpen] = useState(false);
  const dark = mode === 'execution';
  return (
    <div style={{
      flexShrink: 0,
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 22px',
      background: dark ? '#13131A' : '#FFFFFF',
      borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(15,15,30,0.06)'}`,
      color: dark ? '#F5F5F7' : '#0F0F1A',
    }}>
      <div style={{
        display: 'inline-flex', padding: 3, gap: 2,
        background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(15,15,30,0.04)',
        border: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'rgba(15,15,30,0.08)'}`,
        borderRadius: 999,
      }}>
        <ModeBtn
          active={mode === 'office'}
          onClick={() => setMode('office')}
          Icon={Building2}
          label="Office"
          dark={dark}
          testid="mode-office"
        />
        <ModeBtn
          active={mode === 'execution'}
          onClick={() => canExecute && setMode('execution')}
          Icon={Briefcase}
          label="Execution"
          dark={dark}
          disabled={!canExecute}
          testid="mode-execution"
        />
        <ModeBtn
          active={mode === 'workshop'}
          onClick={() => setMode('workshop')}
          Icon={Sparkles}
          label="Workshop"
          dark={dark}
          testid="mode-workshop"
        />
      </div>
      {mode === 'execution' && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setOpen(o => !o)}
            data-testid="exec-project-dropdown"
            style={{
              padding: '7px 13px',
              background: 'rgba(94,234,212,0.12)',
              border: '1px solid rgba(94,234,212,0.4)',
              color: '#5EEAD4',
              fontSize: 11.5, fontWeight: 700, letterSpacing: '-0.005em',
              cursor: 'pointer', fontFamily: 'inherit',
              borderRadius: 999,
              display: 'inline-flex', alignItems: 'center', gap: 7,
            }}
          >
            <Target size={11} /> {active?.name || 'Select a project'} <ChevronDown size={11} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }} />
          </button>
          {open && (
            <>
              <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 50 }} />
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 51,
                width: 320, maxHeight: 360, overflowY: 'auto',
                background: '#FFFFFF',
                border: '1px solid rgba(15,15,30,0.12)',
                borderRadius: 12,
                boxShadow: '0 20px 50px -10px rgba(15,15,30,0.25)',
                padding: 6,
              }}>
                {(overview.ventures || []).map(v => {
                  const ready = executionReadyIds.has(v.monetize_id);
                  return (
                    <button
                      key={v.monetize_id}
                      onClick={() => { if (ready) { setActiveId(v.monetize_id); setOpen(false); } }}
                      disabled={!ready}
                      data-testid={`exec-project-option-${v.monetize_id}`}
                      style={{
                        width: '100%', textAlign: 'left',
                        padding: '8px 10px', marginBottom: 2,
                        background: v.monetize_id === activeId ? 'rgba(94,234,212,0.1)' : 'transparent',
                        border: 'none', borderRadius: 8,
                        cursor: ready ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit',
                        opacity: ready ? 1 : 0.45,
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      <Target size={12} color={ready ? '#5EEAD4' : 'rgba(15,15,30,0.4)'} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {v.name}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.55)', marginTop: 1 }}>
                          {ready ? 'Traction ready — click to execute' : 'Traction not generated yet'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
      {mode === 'office' && (
        <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.55)', letterSpacing: '0.04em' }}>
          Mission Control · all ventures
        </div>
      )}
      {mode === 'workshop' && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          fontSize: 11, color: 'rgba(15,15,30,0.6)', letterSpacing: '0.04em',
        }}>
          <Sparkles size={12} color="#7C3AED" />
          Refine before execution
        </div>
      )}
      {mode === 'execution' && !canExecute && (
        <div style={{ fontSize: 11, color: 'rgba(245,245,247,0.55)' }}>
          Generate the traction layer for a venture to unlock Execution.
        </div>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, Icon, label, dark, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        padding: '6px 13px',
        background: active ? (dark ? '#5EEAD4' : '#0F0F1A') : 'transparent',
        color: active ? (dark ? '#0A0A0B' : '#FFFFFF') : (dark ? 'rgba(245,245,247,0.65)' : 'rgba(15,15,30,0.65)'),
        border: 'none', borderRadius: 999,
        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
        display: 'inline-flex', alignItems: 'center', gap: 5,
        opacity: disabled ? 0.45 : 1,
        transition: 'all 0.15s',
      }}
    >
      <Icon size={11} /> {label}
    </button>
  );
}

/* =================== Top bar with venture dropdown =================== */
function HQTopBar({ overview, activeId, setActiveId, active, hq, autopilot, toggling, onTogglePlay }) {
  const [open, setOpen] = useState(false);
  const audit = hq?.last_run?.audit;
  const focus = hq?.last_run?.next_24h_focus;
  const isPlaying = autopilot?.state === 'play';
  const nextPulse = autopilot?.next_pulse_in_s ?? 0;
  const pulseCount = autopilot?.pulse_count ?? 0;

  return (
    <div style={{
      flexShrink: 0,
      borderBottom: '1px solid rgba(15,15,30,0.06)',
      padding: '14px 22px',
      background: 'rgba(255,215,0,0.025)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
          color: ACCENT, padding: '4px 8px',
          background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.3)',
        }}>
          <Cpu size={11} /> HQ Mission Control
        </span>

        {/* Venture dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setOpen(!open)}
            data-testid="hq-venture-dropdown"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '8px 14px',
              background: 'rgba(15,15,30,0.035)',
              border: '1px solid rgba(15,15,30,0.1)',
              color: '#0F0F1A', fontFamily: 'inherit',
              fontSize: 13, fontWeight: 700,
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >
            <Target size={13} color={ACCENT} />
            <span>{active?.name || 'Select venture'}</span>
            {active && (
              <span style={{
                fontSize: 9, padding: '2px 6px', fontWeight: 700,
                background: 'rgba(15,15,30,0.06)', color: 'rgba(15,15,30,0.72)',
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                {active.tasks_open} open
              </span>
            )}
            <ChevronDown size={12} color="rgba(15,15,30,0.55)" />
          </button>
          {open && (
            <>
              <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 6,
                minWidth: 360, maxHeight: 420, overflowY: 'auto',
                background: '#13131A',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12,
                boxShadow: '0 24px 70px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,215,0,0.04)',
                zIndex: 50,
                padding: 4,
              }}>
                <div style={{
                  padding: '8px 12px 6px',
                  fontSize: 9, fontWeight: 800, letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.45)',
                }}>
                  Switch venture · {overview.ventures.length}
                </div>
                {overview.ventures.map(v => {
                  const sel = v.monetize_id === activeId;
                  return (
                    <button
                      key={v.monetize_id}
                      onClick={() => { setActiveId(v.monetize_id); setOpen(false); }}
                      data-testid={`hq-venture-option-${v.monetize_id}`}
                      style={{
                        width: '100%', padding: '10px 12px',
                        display: 'flex', alignItems: 'center', gap: 11,
                        background: sel ? 'rgba(255,215,0,0.10)' : 'transparent',
                        border: sel ? '1px solid rgba(255,215,0,0.28)' : '1px solid transparent',
                        borderRadius: 9,
                        marginBottom: 2,
                        color: '#F5F5F7', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                        transition: 'all 0.12s',
                      }}
                      onMouseEnter={e => { if (!sel) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                      onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{
                        width: 30, height: 30, flexShrink: 0, borderRadius: 8,
                        background: v.first_dollar_at ? 'rgba(34,197,94,0.18)' : 'rgba(255,215,0,0.15)',
                        border: `1px solid ${v.first_dollar_at ? 'rgba(34,197,94,0.45)' : 'rgba(255,215,0,0.45)'}`,
                        color: v.first_dollar_at ? '#22C55E' : ACCENT,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {v.first_dollar_at ? <Trophy size={13} /> : <Target size={13} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 12.5, fontWeight: 700, color: '#F5F5F7',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          letterSpacing: '-0.005em',
                        }}>
                          {v.name}
                        </div>
                        <div style={{
                          fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 3,
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <span><span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>{v.agents_count}</span> agents</span>
                          <span style={{ color: 'rgba(255,255,255,0.2)' }}>·</span>
                          <span><span style={{ color: '#06B6D4', fontWeight: 700 }}>{v.tasks_open}</span> open</span>
                          <span style={{ color: 'rgba(255,255,255,0.2)' }}>·</span>
                          <span><span style={{ color: '#22C55E', fontWeight: 700 }}>{v.tasks_done}</span> done</span>
                        </div>
                      </div>
                      {sel && <ArrowRight size={12} color={ACCENT} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Quick stats */}
        {active && (
          <div style={{ display: 'flex', gap: 8, marginLeft: 4 }}>
            <Stat color="#06B6D4" label="Open" value={active.tasks_open} />
            <Stat color="#22C55E" label="Done" value={active.tasks_done} />
            <Stat color="#A855F7" label="Msgs/24h" value={active.messages_today} />
            <Stat color={ACCENT} label="Score" value={active.score} />
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Light pulse indicator — only shows when the team is actively
            iterating on a pulse. Removed the office open/close toggle since
            agents now collaborate on demand whenever a mission triggers it,
            no manual gating required. */}
        {isPlaying && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 11, color: '#16A34A',
          }}>
            <span style={{
              width: 6, height: 6, background: '#22C55E', borderRadius: '50%',
              animation: 'pulse-glow 1.4s ease-in-out infinite',
            }} />
            The team's at it · pulse {pulseCount} · next in {nextPulse}s
          </div>
        )}
      </div>

      {/* Audit + focus row */}
      {(audit || focus) && (
        <div style={{
          marginTop: 12, padding: '10px 14px',
          background: 'rgba(15,15,30,0.025)',
          border: '1px solid rgba(15,15,30,0.06)',
          display: 'flex', gap: 14, alignItems: 'flex-start',
        }}>
          {audit && (
            <div style={{ flex: 2 }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: ACCENT, marginBottom: 4 }}>
                Hermes audit
              </div>
              <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.78)', lineHeight: 1.55 }}>
                {audit}
              </div>
            </div>
          )}
          {focus && (
            <div style={{ flex: 1, paddingLeft: 14, borderLeft: audit ? '1px solid rgba(15,15,30,0.08)' : 'none' }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#22C55E', marginBottom: 4 }}>
                Next 24h focus
              </div>
              <div style={{ fontSize: 12, color: '#0F0F1A', fontWeight: 700, lineHeight: 1.5 }}>
                {focus}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ color, label, value }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '6px 10px',
      background: `${color}10`, border: `1px solid ${color}30`,
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.55)' }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color, fontFamily: "'JetBrains Mono', monospace" }}>
        {value}
      </div>
    </div>
  );
}

/* =================== Body: roster + board + feed =================== */
/* =================== HQ OFFICE — Modern light layout ===================
   Layout (top → bottom):
   1. TriumvirateHero — 3 main IAs (Aria / Hermes / Lyra) — the Build /
      Package / Sell trinity. Hermes now owns the full SELL pillar end-to-end
      (lead-gen + outreach + closing, formerly split with Markus).
   2. AgentStoryRing — remaining agents as colorful Instagram-style circles
   3. OfficeWorkRow — Kanban board (left, larger) | Live Feed (right)
   The execution-mode UI is untouched. Only data-visualization is restyled.
   ================================================================== */

const TRIUMVIRATE = {
  hermes: {
    img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/mou7csvz_238e23e8-fd0c-4b2b-ae70-8b1eb646daec.png',
    role: 'The Pathfinder',
    quote: 'I find the right humans and bring them home.',
    accent: '#FFD166',
    gradient: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)',
  },
  builder: {
    img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/oqa19gtc_77de9bac-3f25-4df6-83c9-1bfd45a5acfe.png',
    role: 'The Architect',
    quote: 'I construct the product, line by line.',
    accent: '#7C3AED',
    gradient: 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)',
  },
  lyra: {
    img: 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/a3f3fe57_2f141ba8-6ea7-4405-9fd5-d1d34fa09a9f.png',
    role: 'The Storyteller',
    quote: 'I shape every word, image, and frame.',
    accent: '#FF6B1A',
    gradient: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)',
  },
};

function TriumvirateCard({ id, name, agentKey, taskCount, isLive, onClick }) {
  const cfg = TRIUMVIRATE[id] || TRIUMVIRATE.builder;
  return (
    <div data-testid={`hq-triumvirate-${id}`}
      onClick={() => onClick && onClick(agentKey)}
      style={{
      position: 'relative',
      flex: 1, minWidth: 0,
      background: '#FFFFFF',
      border: '1px solid rgba(15,15,30,0.07)',
      borderRadius: 22,
      overflow: 'hidden',
      transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
      boxShadow: '0 1px 2px rgba(15,15,30,0.04)',
      cursor: onClick ? 'pointer' : 'default',
    }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = `0 18px 40px ${cfg.accent}25`;
        e.currentTarget.style.borderColor = `${cfg.accent}55`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,15,30,0.04)';
        e.currentTarget.style.borderColor = 'rgba(15,15,30,0.07)';
      }}
    >
      {/* Soft glow corner — color signature */}
      <div style={{
        position: 'absolute', top: -50, right: -50,
        width: 200, height: 200, borderRadius: '50%',
        background: `radial-gradient(circle, ${cfg.accent}30 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* HUGE name behind figure — peeks above the head, filled with section navy */}
      <div style={{
        position: 'absolute',
        top: 6, left: 0, right: 0,
        textAlign: 'center',
        fontSize: 'clamp(72px, 9vw, 132px)',
        fontWeight: 900,
        letterSpacing: '-0.045em',
        textTransform: 'uppercase',
        lineHeight: 1,
        color: '#0A1638',
        pointerEvents: 'none',
        userSelect: 'none',
      }}>
        {name}
      </div>

      {/* Figure */}
      <div style={{
        position: 'relative',
        padding: '140px 24px 16px',
        display: 'flex', justifyContent: 'center',
      }}>
        <div style={{
          position: 'relative',
          width: 'clamp(150px, 18vw, 220px)',
          height: 'clamp(150px, 18vw, 220px)',
          borderRadius: '50%',
          overflow: 'hidden',
          boxShadow: `0 20px 50px ${cfg.accent}38, 0 0 0 1px rgba(15,15,30,0.06)`,
        }}>
          <img src={cfg.img} alt={name} style={{
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: 'top center',
          }} />
          {isLive && (
            <span style={{
              position: 'absolute', bottom: 8, right: 8,
              width: 14, height: 14, borderRadius: '50%',
              background: '#22C55E',
              border: '3px solid #FFFFFF',
              boxShadow: '0 0 14px #22C55E',
              animation: 'pulse-glow 1.8s ease-in-out infinite',
            }} />
          )}
        </div>
      </div>

      {/* Identity stack */}
      <div style={{ position: 'relative', padding: '0 24px 22px', textAlign: 'center' }}>
        {/* Role — gradient uppercase, the typography "wrap" below */}
        <div style={{
          fontSize: 'clamp(20px, 2.4vw, 30px)',
          fontWeight: 900,
          letterSpacing: '-0.025em',
          textTransform: 'uppercase',
          background: cfg.gradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          lineHeight: 1.05,
          marginBottom: 10,
        }}>
          {cfg.role}
        </div>

        {/* Name + meta line */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '6px 14px',
          background: `${cfg.accent}14`,
          border: `1px solid ${cfg.accent}40`,
          borderRadius: 999,
          marginBottom: 14,
        }}>
          <span style={{
            fontSize: 11, fontWeight: 800,
            color: cfg.accent,
            textTransform: 'uppercase', letterSpacing: '0.18em',
          }}>
            {name}
          </span>
          <span style={{ width: 1, height: 10, background: `${cfg.accent}40` }} />
          <span style={{
            fontSize: 10, fontWeight: 700,
            color: 'rgba(15,15,30,0.6)',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {taskCount} {taskCount === 1 ? 'task' : 'tasks'}
          </span>
        </div>

        {/* Quote */}
        <div style={{
          fontSize: 13,
          color: 'rgba(15,15,30,0.62)',
          fontStyle: 'italic',
          lineHeight: 1.5,
          maxWidth: 320, margin: '0 auto',
          position: 'relative',
          padding: '0 14px',
        }}>
          <span style={{
            position: 'absolute', left: 0, top: -6,
            fontSize: 24, color: `${cfg.accent}80`, fontWeight: 700,
            fontFamily: 'Georgia, serif', lineHeight: 1,
          }}>&ldquo;</span>
          {cfg.quote}
          <span style={{
            position: 'absolute', right: 0, bottom: -16,
            fontSize: 24, color: `${cfg.accent}80`, fontWeight: 700,
            fontFamily: 'Georgia, serif', lineHeight: 1,
          }}>&rdquo;</span>
        </div>

        {onClick && (
          <div style={{
            marginTop: 18,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 9, fontWeight: 800, letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: cfg.accent,
            padding: '6px 12px',
            background: `${cfg.accent}10`,
            border: `1px solid ${cfg.accent}38`,
            borderRadius: 999,
          }}>
            Open workshop →
          </div>
        )}
      </div>
    </div>
  );
}

function TriumvirateHero({ agents, board, messages, autopilotOn, onOpenWorkshop }) {
  // Count open tasks per agent
  const taskCounts = {};
  for (const t of (board.backlog || []).concat(board.in_progress || []).concat(board.blocked || [])) {
    const id = t.owner_agent_id || 'unassigned';
    taskCounts[id] = (taskCounts[id] || 0) + 1;
  }
  // Live detection — memoized so the Date.now() call is deterministic per render
  const liveAgents = React.useMemo(() => {
    const set = new Set();
    if (!autopilotOn) return set;
    const liveKinds = new Set(['thinking', 'doing', 'engaging']);
    const now = Date.now();
    for (const m of (messages || [])) {
      if (!m.from_agent_id || !liveKinds.has(m.kind)) continue;
      const ts = Date.parse(m.created_at);
      if (!Number.isNaN(ts) && now - ts < 45_000) set.add(m.from_agent_id);
    }
    return set;
  }, [messages, autopilotOn]);
  // Hermes absorbed the former Markus pillar — every legacy task with a
  // markus/marcus/revenue/sales owner is counted under Hermes too so the
  // SELL pillar shows its full workload.
  const hermesTaskCount =
    (taskCounts['hermes'] || 0) +
    (taskCounts['markus'] || 0) +
    (taskCounts['marcus'] || 0) +
    (taskCounts['revenue'] || 0) +
    (taskCounts['sales'] || 0);
  const hermesIsLive =
    liveAgents.has('hermes') ||
    liveAgents.has('markus') ||
    liveAgents.has('marcus') ||
    liveAgents.has('revenue') ||
    liveAgents.has('sales');

  return (
    <div style={{
      padding: '32px 28px 28px',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      background: 'linear-gradient(180deg, #0A1638 0%, #0E1E48 100%)',
    }}>
      {/* Eyebrow band */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 20, padding: '0 4px',
      }}>
        <span style={{ width: 28, height: 1, background: '#7C3AED' }} />
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.3em',
          textTransform: 'uppercase', color: '#7C3AED',
        }}>
          The Council · Build · Package · Sell
        </span>
        <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
      </div>

      {/* 3 hero cards · Aria (Build) · Lyra (Package) · Hermes (Sell) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 14,
      }}>
        <TriumvirateCard
          id="builder"
          name="Aria"
          agentKey="builder"
          taskCount={taskCounts['builder'] || 0}
          isLive={liveAgents.has('builder')}
          onClick={onOpenWorkshop}
        />
        <TriumvirateCard
          id="lyra"
          name="Lyra"
          agentKey="lyra"
          taskCount={taskCounts['lyra'] || taskCounts['content'] || 0}
          isLive={liveAgents.has('lyra') || liveAgents.has('content')}
          onClick={onOpenWorkshop}
        />
        <TriumvirateCard
          id="hermes"
          name="Hermes"
          agentKey="hermes"
          taskCount={hermesTaskCount}
          isLive={hermesIsLive}
          onClick={onOpenWorkshop}
        />
      </div>
    </div>
  );
}

function AgentStoryRing({ agents, board, messages, autopilotOn }) {
  // Filter out the 3 core IAs — they are already in the Triumvirate above.
  // Hermes absorbed Markus, so legacy markus/marcus owners are folded into
  // the Trinity card and must not also surface as side specialists.
  const core = new Set(['hermes', 'builder', 'markus', 'marcus', 'lyra']);
  const others = (agents || []).filter(a => {
    const id = (a.agent_id || '').toLowerCase();
    const name = (a.name || '').toLowerCase();
    if (core.has(id)) return false;
    if (name === 'aria' || name === 'hermes' || name === 'markus' || name === 'marcus' || name === 'lyra') return false;
    return true;
  });

  const taskCounts = {};
  for (const t of (board.backlog || []).concat(board.in_progress || []).concat(board.blocked || [])) {
    const id = t.owner_agent_id || 'unassigned';
    taskCounts[id] = (taskCounts[id] || 0) + 1;
  }
  const liveAgents = React.useMemo(() => {
    const set = new Set();
    if (!autopilotOn) return set;
    const liveKinds = new Set(['thinking', 'doing', 'engaging']);
    const now = Date.now();
    for (const m of (messages || [])) {
      if (!m.from_agent_id || !liveKinds.has(m.kind)) continue;
      const ts = Date.parse(m.created_at);
      if (!Number.isNaN(ts) && now - ts < 45_000) set.add(m.from_agent_id);
    }
    return set;
  }, [messages, autopilotOn]);

  if (others.length === 0) return null;

  return (
    <div style={{
      padding: '20px 28px 24px',
      borderBottom: '1px solid rgba(15,15,30,0.06)',
      background: '#FBFAF7',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 16,
      }}>
        <span style={{ width: 24, height: 1, background: '#FF6B1A' }} />
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.28em',
          textTransform: 'uppercase', color: '#FF6B1A',
        }}>
          The squad · {others.length} specialists on deck
        </span>
        <span style={{ flex: 1, height: 1, background: 'rgba(15,15,30,0.06)' }} />
      </div>

      <div style={{
        display: 'flex', gap: 18,
        overflowX: 'auto',
        paddingBottom: 4,
        scrollbarWidth: 'thin',
      }}>
        {others.map(a => {
          const isLive = liveAgents.has(a.agent_id);
          const count = taskCounts[a.agent_id] || 0;
          return (
            <div
              key={a.agent_id}
              data-testid={`hq-squad-${a.agent_id}`}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 8, minWidth: 76,
                cursor: 'default',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={{
                position: 'relative',
                width: 64, height: 64,
                borderRadius: '50%',
                padding: 3,
                background: `conic-gradient(from 180deg,
                  ${a.color || '#7C3AED'} 0deg,
                  #FF6B1A 90deg,
                  #C026D3 180deg,
                  #FFD166 270deg,
                  ${a.color || '#7C3AED'} 360deg)`,
              }}>
                <div style={{
                  width: '100%', height: '100%',
                  borderRadius: '50%',
                  padding: 2,
                  background: '#FBFAF7',
                }}>
                  <div style={{
                    width: '100%', height: '100%',
                    borderRadius: '50%',
                    background: `${a.color || '#7C3AED'}1A`,
                    border: `1px solid ${a.color || '#7C3AED'}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: a.color || '#7C3AED',
                    fontSize: 18, fontWeight: 800,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {(a.avatar || (a.name || 'A').slice(0, 1)).toUpperCase()}
                  </div>
                </div>
                {isLive && (
                  <span style={{
                    position: 'absolute', bottom: 1, right: 1,
                    width: 14, height: 14, borderRadius: '50%',
                    background: '#22C55E',
                    border: '2px solid #FBFAF7',
                    boxShadow: '0 0 10px #22C55E',
                  }} />
                )}
                {count > 0 && (
                  <span style={{
                    position: 'absolute', top: -2, right: -2,
                    minWidth: 18, height: 18, padding: '0 5px',
                    borderRadius: 9,
                    background: a.color || '#7C3AED',
                    color: '#fff',
                    fontSize: 10, fontWeight: 800,
                    fontFamily: "'JetBrains Mono', monospace",
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(15,15,30,0.18)',
                  }}>
                    {count}
                  </span>
                )}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 700,
                color: isLive ? (a.color || '#7C3AED') : '#0F0F1A',
                maxWidth: 80, textAlign: 'center',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                letterSpacing: '-0.005em',
              }}>
                {a.name}
              </div>
              <div style={{
                fontSize: 9, fontWeight: 600,
                color: 'rgba(15,15,30,0.45)',
                textTransform: 'uppercase', letterSpacing: '0.08em',
                maxWidth: 80, textAlign: 'center',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {a.kind_label || a.role || a.kind || '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HQBody({ hq, autopilot, onTaskChanged, onPostMessage, onOpenWorkshop, onSwitchToExecution }) {
  // Project id derived from any task or the venture context
  const projectId = hq?.project_id || hq?.monetize_id || (hq?.tasks?.[0]?.project_id) || (hq?.tasks?.[0]?.monetize_id);
  // Flatten tasks for the Loop Kanban (all states · 1 array)
  const allTasks = React.useMemo(() => {
    if (Array.isArray(hq?.tasks)) return hq.tasks;
    if (hq?.board) {
      return [
        ...(hq.board.backlog || []),
        ...(hq.board.in_progress || []),
        ...(hq.board.blocked || []),
        ...(hq.board.done || []),
      ];
    }
    return [];
  }, [hq]);

  // Local state for autonomous run + validation modal
  const [reviewTask, setReviewTask] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const runTask = useCallback(async (task) => {
    if (!task?.task_id || runningId) return;
    setRunningId(task.task_id);
    try {
      const r = await fetch(`${API}/api/tasks/${task.task_id}/run`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (r.ok) {
        const data = await r.json();
        // Open Validation Modal directly on the delivered artifact.
        setReviewTask({ ...task, ...data, current_state: 'review' });
      }
    } finally {
      setRunningId(null);
      onTaskChanged?.();
    }
  }, [runningId, onTaskChanged]);

  return (
    <div style={{
      flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden',
      display: 'flex', flexDirection: 'column',
      background: '#F4F3F0',
    }}>
      <TriumvirateHero
        agents={hq.agents}
        board={hq.board}
        messages={hq.messages}
        autopilotOn={autopilot?.on}
        onOpenWorkshop={onOpenWorkshop}
      />
      {/* Live Pulse Bar — Aria · Lyra · Hermes states · polls every 6s */}
      <AgentPulseBar projectId={projectId} />
      {/* Live Drift Banner — pulses red when code ↔ plan disagree.
          Click → switch to Execution to answer the drift question. */}
      <DriftBanner projectId={projectId} onResolve={onSwitchToExecution} />
      {/* New Office layout: 7-column Kanban + Live Stream side-by-side. */}
      <div style={{
        display: 'flex', gap: 0,
        flex: 1, minHeight: '60vh',
      }}>
        <LoopKanban
          projectId={projectId}
          tasks={allTasks}
          onReviewTask={setReviewTask}
        />
        <LiveActivityStream
          projectId={projectId}
          onTaskClick={() => { /* the kanban now owns the timeline drawer */ }}
        />
      </div>
      {reviewTask && (
        <ValidationModal
          task={reviewTask}
          onClose={() => setReviewTask(null)}
          onDone={() => { setReviewTask(null); onTaskChanged?.(); }}
        />
      )}
    </div>
  );
}

/* =================== Mission roster (left) =================== */
function RosterRail({ agents, board, messages, autopilotOn }) {
  // Build a list including Hermes + builder + project agents
  const allAgents = [
    { agent_id: 'hermes', name: 'Hermes', role: 'Orchestrator', kind: 'orchestrator', color: '#FFD700', avatar: 'H' },
    { agent_id: 'builder', name: 'Aria', role: 'Builder & Designer', kind: 'builder', color: '#7C3AED', avatar: 'A' },
    ...agents,
  ];
  // Count tasks per agent
  const taskCounts = {};
  for (const t of (board.backlog || []).concat(board.in_progress || []).concat(board.blocked || [])) {
    const id = t.owner_agent_id || 'unassigned';
    taskCounts[id] = (taskCounts[id] || 0) + 1;
  }
  // Detect which agents are currently "live" (latest status message younger
  // than 45s and kind ∈ {thinking, doing, engaging}). These agents get a
  // breathing ring on their avatar so the user sees who's at work. We
  // ONLY consider this when the office is open — when it's closed, the
  // roster should look completely calm.
  const liveAgents = new Set();
  if (autopilotOn) {
    const liveKinds = new Set(['thinking', 'doing', 'engaging']);
    const now = Date.now();
    for (const m of (messages || [])) {
      if (!m.from_agent_id || !liveKinds.has(m.kind)) continue;
      try {
        if (now - new Date(m.created_at).getTime() < 45_000) {
          liveAgents.add(m.from_agent_id);
        }
      } catch (_) { /* ignore parse errors */ }
    }
  }

  return (
    <div style={{
      borderRight: '1px solid rgba(15,15,30,0.06)',
      padding: '14px 12px',
      overflowY: 'auto',
      background: 'rgba(15,15,30,0.015)',
    }}>
      <div style={{
        fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: 'rgba(15,15,30,0.48)', padding: '0 4px 8px',
      }}>
        Mission roster · {allAgents.length}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {allAgents.map(a => {
          const open = taskCounts[a.agent_id] || 0;
          const isLive = liveAgents.has(a.agent_id);
          return (
            <div key={a.agent_id} data-testid={`roster-${a.agent_id}`} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 8px',
              background: isLive ? `${a.color}0E` : 'transparent',
              border: `1px solid ${isLive ? `${a.color}33` : 'transparent'}`,
              transition: 'all 0.25s',
            }}>
              <div style={{
                width: 30, height: 30, flexShrink: 0, position: 'relative',
                background: `${a.color}20`, color: a.color,
                border: `1px solid ${a.color}45`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 800,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                {a.avatar || (a.name || '').slice(0, 2).toUpperCase()}
                <span
                  className={isLive ? 'hq-working-dot' : undefined}
                  style={{
                    position: 'absolute', bottom: -2, right: -2,
                    width: 9, height: 9,
                    background: isLive ? a.color : '#22C55E',
                    color: isLive ? a.color : '#22C55E',
                    border: '2px solid #FBFAF7',
                    animation: isLive ? undefined : 'pulse-glow 1.8s ease-in-out infinite',
                  }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 12, fontWeight: 700,
                  color: isLive ? a.color : '#0F0F1A',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  transition: 'color 0.2s',
                }}>
                  {a.name}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {isLive && open > 0 ? (
                    <span style={{
                      fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: 9,
                      color: a.color,
                    }}>
                      at work…
                    </span>
                  ) : (
                    a.kind_label || a.role || a.kind
                  )}
                </div>
              </div>
              {open > 0 && (
                <span style={{
                  fontSize: 9, fontWeight: 800, padding: '2px 6px',
                  background: `${a.color}20`, color: a.color,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {open}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =================== Trello board (center) =================== */
function BoardColumn({ hq, onTaskChanged }) {
  const [openTask, setOpenTask] = useState(null);
  const cols = ['backlog', 'in_progress', 'blocked', 'done'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
      <div style={{
        padding: '12px 18px',
        borderBottom: '1px solid rgba(15,15,30,0.06)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.65)' }}>
          Tasks board
        </div>
        <div style={{ flex: 1, height: 1, background: 'rgba(15,15,30,0.06)' }} />
        <div style={{ fontSize: 10, color: 'rgba(15,15,30,0.48)' }}>
          <span key={hq.tasks.length} className="hq-count-flip" style={{ fontVariantNumeric: 'tabular-nums' }}>{hq.tasks.length}</span> total · <span key={`open-${hq.tasks.filter(t => t.status !== 'done').length}`} className="hq-count-flip" style={{ fontVariantNumeric: 'tabular-nums' }}>{hq.tasks.filter(t => t.status !== 'done').length}</span> open
        </div>
      </div>
      <div style={{
        flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 10, padding: 12, overflowY: 'auto', minHeight: 0,
      }}>
        {cols.map(c => (
          <BoardCol
            key={c}
            colKey={c}
            tasks={hq.board[c] || []}
            onOpen={t => setOpenTask(t)}
          />
        ))}
      </div>
      {openTask && (
        <TaskDrawer
          task={openTask}
          onClose={() => setOpenTask(null)}
          onChanged={() => { setOpenTask(null); onTaskChanged(); }}
        />
      )}
    </div>
  );
}

function BoardCol({ colKey, tasks, onOpen }) {
  const def = STATUS_DEFS[colKey];
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      background: 'rgba(15,15,30,0.02)',
      border: '1px solid rgba(15,15,30,0.045)',
      minHeight: 0,
    }}>
      <div style={{
        padding: '10px 12px',
        borderBottom: '1px solid rgba(15,15,30,0.045)',
        display: 'flex', alignItems: 'center', gap: 7,
      }}>
        <span style={{ width: 6, height: 6, background: def.color }} />
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: def.color }}>
          {def.label}
        </div>
        <span key={tasks.length} className="hq-count-flip" style={{
          fontSize: 9, padding: '2px 6px', fontWeight: 700,
          background: 'rgba(15,15,30,0.045)', color: 'rgba(15,15,30,0.6)',
          fontFamily: "'JetBrains Mono', monospace", marginLeft: 'auto',
        }}>{tasks.length}</span>
      </div>
      <div style={{ flex: 1, padding: 8, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {tasks.length === 0 ? (
          <div style={{ padding: '20px 6px', textAlign: 'center', fontSize: 10, color: 'rgba(15,15,30,0.22)' }}>
            empty
          </div>
        ) : (
          tasks.map(t => <TaskCard key={t.task_id} task={t} onOpen={() => onOpen(t)} />)
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, onOpen }) {
  const kind = KIND_BADGES[task.kind] || { label: task.kind, color: '#94A3B8' };
  const overdue = task.deadline_at && new Date(task.deadline_at) < new Date() && task.status !== 'done';
  const isBlocked = task.status === 'blocked';
  const hasDeliverable = task.status === 'done' && task.agent_output;
  const cardAccent = isBlocked ? '#FF6B6B' : hasDeliverable ? '#22C55E' : (task.owner_color || ACCENT);
  return (
    <button
      onClick={onOpen}
      data-testid={`task-card-${task.task_id}`}
      className="hq-card-row"
      style={{
        textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
        padding: 10, color: '#0F0F1A',
        background: 'rgba(15,15,30,0.025)',
        border: `1px solid ${overdue ? 'rgba(255,107,107,0.4)' : isBlocked ? 'rgba(255,107,107,0.3)' : 'rgba(15,15,30,0.06)'}`,
        display: 'flex', flexDirection: 'column', gap: 6,
        transition: 'all 0.12s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = `${cardAccent}55`; e.currentTarget.style.background = 'rgba(15,15,30,0.035)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = overdue ? 'rgba(255,107,107,0.4)' : isBlocked ? 'rgba(255,107,107,0.3)' : 'rgba(15,15,30,0.06)'; e.currentTarget.style.background = 'rgba(15,15,30,0.025)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 8, padding: '2px 5px', fontWeight: 800,
          background: task.priority === 'P0' ? '#FF6B6B' : task.priority === 'P1' ? ACCENT : 'rgba(15,15,30,0.08)',
          color: task.priority === 'P0' ? 'var(--studio-bg)' : task.priority === 'P1' ? 'var(--studio-bg)' : 'rgba(15,15,30,0.72)',
          fontFamily: "'JetBrains Mono', monospace",
        }}>{task.priority || 'P2'}</span>
        <span style={{
          fontSize: 9, padding: '2px 6px', fontWeight: 700,
          background: `${kind.color}20`, color: kind.color,
          border: `1px solid ${kind.color}40`,
          letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>{kind.label}</span>
        {isBlocked && (
          <span style={{
            fontSize: 8, padding: '2px 5px', fontWeight: 800,
            background: 'rgba(255,107,107,0.18)', color: '#FF6B6B',
            border: '1px solid rgba(255,107,107,0.4)',
            letterSpacing: '0.08em', display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            <AlertOctagon size={9} /> ACTION REQUIRED
          </span>
        )}
        {hasDeliverable && (
          <span style={{
            fontSize: 8, padding: '2px 5px', fontWeight: 800,
            background: 'rgba(34,197,94,0.18)', color: '#22C55E',
            border: '1px solid rgba(34,197,94,0.4)',
            letterSpacing: '0.08em',
          }}>SHIPPED</span>
        )}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.4, color: '#0F0F1A' }}>
        {task.title}
      </div>
      {isBlocked && task.human_action && (
        <div style={{
          fontSize: 10, color: '#FF8A8A', lineHeight: 1.4,
          padding: '6px 8px',
          background: 'rgba(255,107,107,0.06)',
          border: '1px solid rgba(255,107,107,0.18)',
          maxHeight: 50, overflow: 'hidden',
          fontStyle: 'italic',
        }}>
          {task.human_action.slice(0, 110)}{task.human_action.length > 110 ? '…' : ''}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
        <div style={{
          width: 18, height: 18,
          background: `${task.owner_color}22`, color: task.owner_color,
          border: `1px solid ${task.owner_color}50`,
          fontSize: 8, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{(task.owner_name || '?').slice(0, 2).toUpperCase()}</div>
        <span style={{ fontSize: 10, color: 'rgba(15,15,30,0.6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {task.owner_name}
        </span>
        {task.deadline_at && (
          <span style={{
            marginLeft: 'auto', fontSize: 9,
            color: overdue ? '#FF6B6B' : 'rgba(15,15,30,0.48)',
            fontFamily: "'JetBrains Mono', monospace",
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            <Clock size={9} /> {fmtDeadline(task.deadline_at)}
          </span>
        )}
      </div>
      {/* Created at */}
      {task.created_at && (
        <div style={{
          fontSize: 9, color: 'rgba(15,15,30,0.4)',
          fontFamily: "'JetBrains Mono', monospace",
          display: 'flex', alignItems: 'center', gap: 4,
          paddingTop: 4, marginTop: 2,
          borderTop: '1px dashed rgba(15,15,30,0.045)',
          letterSpacing: '0.02em',
        }}>
          <Calendar size={9} />
          <span>created {fmtCreatedAt(task.created_at)}</span>
        </div>
      )}
    </button>
  );
}

function fmtDeadline(iso) {
  try {
    const d = new Date(iso);
    const ms = d - new Date();
    const h = Math.round(ms / 3600000);
    if (h < 0) return `+${-h}h`;
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
  } catch { return ''; }
}

function fmtCreatedAt(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMin = (now - d) / 60000;
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    if (diffMin < 1) return `just now · ${time}`;
    if (diffMin < 60) return `${Math.round(diffMin)}m ago · ${time}`;
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return `today · ${time}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return `yesterday · ${time}`;
    const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `${date} · ${time}`;
  } catch { return ''; }
}

/* =================== Task drawer =================== */
function TaskDrawer({ task, onClose, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [execErr, setExecErr] = useState(null);

  const updateStatus = async (status) => {
    setBusy(status);
    try {
      await fetch(`${API}/api/hq/tasks/${task.task_id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      onChanged();
    } finally { setBusy(null); }
  };
  const execute = async () => {
    setBusy('execute');
    setExecErr(null);
    try {
      const r = await fetch(`${API}/api/hq/tasks/${task.task_id}/execute`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setExecErr(j.detail || `Execute failed (HTTP ${r.status})`);
        return;
      }
      // 202 → background execution scheduled. Poll for completion (the
      // LLM call can take 30-90s, longer than the ingress timeout). We
      // refresh the parent immediately so the card flips to IN_PROGRESS,
      // then poll the task every 3s for up to 3 minutes.
      onChanged();
      const startedAt = Date.now();
      const MAX_MS = 180_000;
      while (Date.now() - startedAt < MAX_MS) {
        await new Promise(res => setTimeout(res, 3000));
        try {
          const tr = await fetch(`${API}/api/hq/tasks/${task.task_id}`, {
            credentials: 'include',
          });
          if (!tr.ok) continue;
          const updated = await tr.json();
          if (updated.status === 'done') {
            onChanged();
            return;
          }
          if (updated.status === 'blocked' && updated.last_execute_error) {
            setExecErr(updated.last_execute_error);
            onChanged();
            return;
          }
        } catch (_) { /* keep polling */ }
      }
      setExecErr(`${task.owner_name || 'Agent'} is taking longer than expected. Check back in a minute or try Execute again.`);
    } finally { setBusy(null); }
  };
  const unblock = async () => {
    setBusy('unblock');
    try {
      await fetch(`${API}/api/hq/tasks/${task.task_id}/unblock`, {
        method: 'POST', credentials: 'include',
      });
      onChanged();
    } finally { setBusy(null); }
  };
  const remove = async () => {
    setBusy('delete');
    try {
      await fetch(`${API}/api/hq/tasks/${task.task_id}`, {
        method: 'DELETE', credentials: 'include',
      });
      onChanged();
    } finally { setBusy(null); }
  };

  const kind = KIND_BADGES[task.kind] || { label: task.kind, color: '#94A3B8' };
  const isBlocked = task.status === 'blocked';
  const hasDeliverable = task.agent_output;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(8px)', zIndex: 100,
      display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
    }}>
      <div onClick={e => e.stopPropagation()} data-testid="task-drawer" style={{
        width: '100%', maxWidth: 560, height: '100%',
        background: '#FFFFFF',
        borderLeft: `1px solid ${task.owner_color}40`,
        boxShadow: '-24px 0 60px rgba(15,15,30,0.18)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: 18, borderBottom: '1px solid rgba(15,15,30,0.06)',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{
            width: 38, height: 38, flexShrink: 0,
            background: `${task.owner_color}20`, color: task.owner_color,
            border: `1px solid ${task.owner_color}45`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
          }}>
            {(task.owner_name || '?').slice(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.5)' }}>
              {task.owner_name} · {task.priority}
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2, lineHeight: 1.35 }}>
              {task.title}
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: 9, padding: '2px 7px', fontWeight: 700,
                background: `${kind.color}20`, color: kind.color,
                border: `1px solid ${kind.color}40`,
                letterSpacing: '0.05em', textTransform: 'uppercase',
              }}>{kind.label}</span>
              <span style={{
                fontSize: 9, padding: '2px 7px', fontWeight: 700,
                background: `${STATUS_DEFS[task.status]?.color}20`,
                color: STATUS_DEFS[task.status]?.color,
                border: `1px solid ${STATUS_DEFS[task.status]?.color}40`,
                letterSpacing: '0.05em', textTransform: 'uppercase',
              }}>{STATUS_DEFS[task.status]?.label}</span>
            </div>
          </div>
          <button onClick={onClose} style={iconBtn}><X size={13} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {/* ACTION REQUIRED block (blocked tasks) */}
          {isBlocked && (task.human_action || task.owner_agent_id) && (
            <div style={{
              marginBottom: 18, padding: 14,
              background: 'rgba(255,107,107,0.06)',
              border: '1px solid rgba(255,107,107,0.4)',
              boxShadow: '0 0 24px rgba(255,107,107,0.08)',
            }}>
              <div style={{
                fontSize: 9, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase',
                color: '#FF6B6B', marginBottom: 8,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>
                <AlertOctagon size={11} /> Action required from you
              </div>
              {task.human_action && (
                <div style={{
                  fontSize: 12, color: 'rgba(15,15,30,0.85)', lineHeight: 1.65,
                  whiteSpace: 'pre-wrap',
                }}>
                  {task.human_action}
                </div>
              )}
              {task.human_action_target && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 9, color: 'rgba(15,15,30,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                    target
                  </span>
                  <span style={{
                    fontSize: 9, padding: '2px 7px', fontWeight: 700,
                    background: 'rgba(15,15,30,0.035)', color: '#0F0F1A',
                    border: '1px solid rgba(15,15,30,0.08)',
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}>{task.human_action_target}</span>
                </div>
              )}
              <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {task.owner_agent_id && (
                  <button
                    onClick={execute}
                    disabled={!!busy || task.status === 'in_progress'}
                    data-testid="task-execute-btn"
                    style={{
                      padding: '10px 16px',
                      background: task.owner_color || '#FFD700',
                      color: '#0F0F1A',
                      border: 'none', cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                      fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      boxShadow: `0 8px 22px ${task.owner_color || '#FFD700'}40`,
                      opacity: (busy || task.status === 'in_progress') ? 0.85 : 1,
                    }}
                  >
                    {busy === 'execute' || task.status === 'in_progress'
                      ? <><Loader2 size={11} style={{ animation: 'spin 0.7s linear infinite' }} /> {task.executing_by || task.owner_name || 'Agent'} is working…</>
                      : <><Sparkles size={11} /> Let {task.owner_name || 'agent'} do it</>}
                  </button>
                )}
                <button
                  onClick={unblock} disabled={!!busy}
                  data-testid="task-unblock-btn"
                  style={{
                    padding: '10px 16px',
                    background: task.owner_agent_id ? 'transparent' : '#22C55E',
                    color: task.owner_agent_id ? 'rgba(15,15,30,0.7)' : '#FFFFFF',
                    border: task.owner_agent_id ? '1px solid rgba(15,15,30,0.18)' : 'none',
                    cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                    fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {busy === 'unblock'
                    ? <><Loader2 size={11} style={{ animation: 'spin 0.7s linear infinite' }} /> Unblocking</>
                    : <><CheckCircle2 size={11} /> I did it manually</>}
                </button>
              </div>
              {(execErr || task.last_execute_error) && (
                <div style={{
                  marginTop: 10, fontSize: 11, color: '#B91C1C',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  <AlertOctagon size={11} /> {execErr || task.last_execute_error}
                </div>
              )}
            </div>
          )}

          {/* Deliverable (agent_text done tasks) */}
          {hasDeliverable && (
            <div style={{
              marginBottom: 18, padding: 14,
              background: 'rgba(34,197,94,0.05)',
              border: '1px solid rgba(34,197,94,0.3)',
            }}>
              <div style={{
                fontSize: 9, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase',
                color: '#22C55E', marginBottom: 8,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}>
                <CheckCircle2 size={11} /> Deliverable from {task.owner_name}
              </div>
              <div style={{
                fontSize: 12, color: 'rgba(15,15,30,0.85)', lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                fontFamily: "system-ui, sans-serif",
              }}>
                {task.agent_output}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(task.agent_output);
                }}
                data-testid="task-copy-deliverable"
                style={{
                  marginTop: 12, padding: '7px 12px',
                  background: 'transparent', color: '#22C55E',
                  border: '1px solid rgba(34,197,94,0.4)',
                  cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}
              >
                Copy
              </button>
            </div>
          )}

          {task.description && (
            <div style={{ marginBottom: 16 }}>
              <Lbl>Why this matters</Lbl>
              <div style={{ fontSize: 13, color: 'rgba(15,15,30,0.78)', lineHeight: 1.6 }}>{task.description}</div>
            </div>
          )}
          {task.expected_outcome && (
            <div style={{ marginBottom: 16 }}>
              <Lbl>Expected outcome</Lbl>
              <div style={{
                fontSize: 12, color: 'rgba(15,15,30,0.78)',
                padding: 10,
                background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.25)',
              }}>{task.expected_outcome}</div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Meta label="Created" value={task.created_at ? fmtCreatedAt(task.created_at) : '—'} />
            <Meta label="Deadline" value={task.deadline_at ? new Date(task.deadline_at).toLocaleString() : '—'} />
            <Meta label="Execution" value={task.execution || 'unknown'} />
            <Meta label="Created by" value={task.created_by || '—'} />
          </div>
        </div>

        <div style={{
          padding: 14, borderTop: '1px solid rgba(15,15,30,0.06)',
          display: 'flex', flexWrap: 'wrap', gap: 6,
        }}>
          {['backlog', 'in_progress', 'blocked', 'done'].filter(s => s !== task.status).map(s => {
            const def = STATUS_DEFS[s];
            return (
              <button
                key={s}
                onClick={() => updateStatus(s)}
                disabled={!!busy}
                data-testid={`task-status-${s}`}
                style={{
                  padding: '7px 12px', fontSize: 10, fontWeight: 700,
                  background: 'transparent', color: def.color,
                  border: `1px solid ${def.color}40`,
                  cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
                  letterSpacing: '0.05em', textTransform: 'uppercase',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                }}
              >
                {busy === s ? <Loader2 size={10} style={{ animation: 'spin 0.7s linear infinite' }} /> : <ArrowRight size={10} />}
                {def.label}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <button onClick={remove} disabled={!!busy} style={{
            padding: '7px 12px', fontSize: 10, fontWeight: 700,
            background: 'transparent', color: 'rgba(255,107,107,0.8)',
            border: '1px solid rgba(255,107,107,0.3)',
            cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit',
            letterSpacing: '0.05em', textTransform: 'uppercase',
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
            {busy === 'delete' ? <Loader2 size={10} style={{ animation: 'spin 0.7s linear infinite' }} /> : <Trash2 size={10} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function Lbl({ children }) {
  return <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.48)', marginBottom: 6 }}>{children}</div>;
}
function Meta({ label, value }) {
  return (
    <div style={{ padding: 10, background: 'rgba(15,15,30,0.025)', border: '1px solid rgba(15,15,30,0.045)' }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.48)' }}>{label}</div>
      <div style={{ fontSize: 11, color: '#0F0F1A', marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    </div>
  );
}
const iconBtn = {
  width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(15,15,30,0.035)', border: '1px solid rgba(15,15,30,0.08)',
  color: 'rgba(15,15,30,0.65)', cursor: 'pointer', fontFamily: 'inherit',
};

/* =================== Live feed (right) =================== */
function FeedColumn({ messages, missions = {}, autopilotOn, onPostMessage }) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await onPostMessage(draft.trim());
      setDraft('');
    } finally { setSending(false); }
  };

  return (
    <div style={{
      borderLeft: '1px solid rgba(15,15,30,0.06)',
      display: 'flex', flexDirection: 'column',
      minHeight: 0,
    }}>
      <div style={{
        padding: '12px 14px', borderBottom: '1px solid rgba(15,15,30,0.06)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <MessageCircle size={13} color={ACCENT} />
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.65)' }}>
          Live feed
        </div>
        <div style={{ flex: 1, height: 1, background: 'rgba(15,15,30,0.06)' }} />
        <div style={{ fontSize: 10, color: '#22C55E', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 6, height: 6, background: '#22C55E', borderRadius: '50%', animation: 'pulse-glow 1.5s ease-in-out infinite' }} />
          live
        </div>
      </div>

      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto', padding: '10px 12px',
        display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0,
      }}>
        {messages.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'rgba(15,15,30,0.45)', fontSize: 11 }}>
            No messages yet. Run the orchestrator to spark agent dialogue.
          </div>
        ) : (() => {
          // The "thinking…" dots should ONLY animate on the most recent
          // live-status message of each agent — and only if it's fresh
          // (<45s old) AND the office is currently open. As soon as a new
          // status arrives, the previous one stops dancing.
          const latestLiveIdPerAgent = {};
          if (autopilotOn) {
            const now = Date.now();
            for (let i = messages.length - 1; i >= 0; i--) {
              const m = messages[i];
              const k = m.kind;
              if (k !== 'thinking' && k !== 'doing' && k !== 'engaging') continue;
              const aid = m.from_agent_id || '_';
              if (latestLiveIdPerAgent[aid]) continue;
              try {
                if (now - new Date(m.created_at).getTime() < 45_000) {
                  latestLiveIdPerAgent[aid] = m.message_id;
                }
              } catch (_) { /* skip */ }
            }
          }
          const liveIdsSet = new Set(Object.values(latestLiveIdPerAgent));
          // ── Group consecutive messages by mission_id so each multi-IA
          //    discussion renders as a visual block with a clean header.
          const groups = [];
          messages.forEach(m => {
            const mid = m.mission_id || null;
            const last = groups[groups.length - 1];
            if (last && last.mission_id === mid) {
              last.items.push(m);
            } else {
              groups.push({ mission_id: mid, items: [m] });
            }
          });
          return groups.map((g, gi) => {
            if (!g.mission_id) {
              // Loose messages outside any mission — render flat.
              return g.items.map(m => (
                <FeedMessage key={m.message_id} m={m} isLiveActive={liveIdsSet.has(m.message_id)} />
              ));
            }
            const mission = missions?.[g.mission_id];
            const participants = new Set(g.items.filter(x => x.from_kind !== 'user').map(x => x.from_name));
            const colorByName = {};
            g.items.forEach(x => { if (x.from_color && x.from_name) colorByName[x.from_name] = x.from_color; });
            const status = mission?.status || g.items[g.items.length - 1]?.kind || '';
            const statusPill = {
              brainstorming: { bg: 'rgba(168,85,247,0.12)', fg: '#7E22CE', label: 'Brainstorming' },
              executing:     { bg: 'rgba(34,211,238,0.14)', fg: '#0E7490', label: 'Executing' },
              awaiting_approval: { bg: 'rgba(250,176,32,0.16)', fg: '#B45309', label: 'Awaiting approval' },
              approved:      { bg: 'rgba(124,58,237,0.14)', fg: '#047857', label: 'Approved' },
              failed:        { bg: 'rgba(248,113,113,0.14)', fg: '#B91C1C', label: 'Failed' },
            }[status] || { bg: 'rgba(15,15,30,0.07)', fg: '#3F3F4A', label: status || '—' };
            return (
              <div key={`mg-${g.mission_id}-${gi}`} data-testid={`mission-group-${g.mission_id}`} style={{
                position: 'relative',
                margin: '10px 0',
                padding: '12px 14px 10px 18px',
                background: 'linear-gradient(180deg, rgba(124,58,237,0.04) 0%, rgba(124,58,237,0.015) 60%, transparent 100%)',
                borderLeft: '2px solid #A855F7',
                borderRadius: '0 12px 12px 0',
              }}>
                {/* Mission header */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  paddingBottom: 10, marginBottom: 8,
                  borderBottom: '1px dashed rgba(124,58,237,0.18)',
                }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.16em',
                    textTransform: 'uppercase', color: '#7E22CE',
                  }}>
                    Mission
                  </span>
                  <span style={{
                    flex: 1, fontSize: 13, fontWeight: 700, color: '#1F1535',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {mission?.title || 'Discussion'}
                  </span>
                  {/* Participant avatars */}
                  <div style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}>
                    {[...participants].slice(0, 5).map((name, i) => (
                      <span key={name} title={name} style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: colorByName[name] || '#A855F7',
                        color: '#fff', fontSize: 10, fontWeight: 800,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        border: '2px solid #fff', marginLeft: i === 0 ? 0 : -7,
                        boxShadow: '0 1px 2px rgba(15,15,30,0.12)',
                      }}>
                        {name.slice(0, 1).toUpperCase()}
                      </span>
                    ))}
                    <span style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 600,
                      color: 'rgba(15,15,30,0.55)',
                    }}>
                      {participants.size} IA{participants.size > 1 ? 's' : ''}
                    </span>
                  </div>
                  <span style={{
                    padding: '3px 9px', borderRadius: 999,
                    background: statusPill.bg, color: statusPill.fg,
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                    textTransform: 'uppercase', whiteSpace: 'nowrap',
                  }}>
                    {statusPill.label}
                  </span>
                </div>
                {g.items.map(m => (
                  <FeedMessage key={m.message_id} m={m} isLiveActive={liveIdsSet.has(m.message_id)} />
                ))}
              </div>
            );
          });
        })()}
      </div>

      <div style={{
        padding: 12,
        borderTop: '1px solid rgba(15,15,30,0.06)',
        background: 'linear-gradient(180deg, transparent, rgba(15,15,30,0.012))',
        display: 'flex', gap: 8, alignItems: 'center',
      }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !sending) send(); }}
          placeholder="Drop a note for the team…"
          data-testid="hq-feed-input"
          onFocus={e => {
            e.target.style.borderColor = ACCENT;
            e.target.style.boxShadow = `0 0 0 3px ${ACCENT}1F`;
          }}
          onBlur={e => {
            e.target.style.borderColor = 'rgba(15,15,30,0.14)';
            e.target.style.boxShadow = 'none';
          }}
          style={{
            flex: 1, padding: '9px 12px',
            background: '#FFFFFF',
            border: '1px solid rgba(15,15,30,0.14)',
            borderRadius: 6,
            color: '#0F0F1A', fontFamily: 'inherit', fontSize: 12.5,
            fontWeight: 500, lineHeight: 1.4,
            outline: 'none',
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
        />
        <button
          onClick={send} disabled={sending || !draft.trim()}
          data-testid="hq-feed-send"
          style={{
            width: 38, height: 36,
            background: draft.trim() ? ACCENT : 'rgba(15,15,30,0.06)',
            color: draft.trim() ? '#0F0F1A' : 'rgba(15,15,30,0.32)',
            border: 'none', borderRadius: 6,
            cursor: draft.trim() ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'inherit',
            boxShadow: draft.trim() ? `0 4px 14px ${ACCENT}55` : 'none',
            transition: 'all 0.15s',
          }}
        >
          {sending ? <Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} /> : <Send size={13} />}
        </button>
      </div>
    </div>
  );
}

function FeedMessage({ m, isLiveActive }) {
  const isUser = m.from_kind === 'user';
  const isAudit = m.kind === 'audit';
  const isHermes = m.from_agent_id === 'hermes';
  const isSystem = m.kind === 'system';
  const isLiveStatus = m.kind === 'thinking' || m.kind === 'doing' ||
                       m.kind === 'engaging' || m.kind === 'done_status' ||
                       m.kind === 'alert';
  const accent = m.from_color || ACCENT;

  if (isLiveStatus) {
    const dotColor = m.kind === 'alert' ? '#FF6B6B'
                   : m.kind === 'done_status' ? '#22C55E'
                   : accent;
    const label = m.kind === 'thinking' ? 'thinking'
                : m.kind === 'doing' ? 'working'
                : m.kind === 'engaging' ? 'engaging'
                : m.kind === 'done_status' ? 'shipped'
                : 'alert';
    const isStillLive = !!isLiveActive && (m.kind === 'thinking' || m.kind === 'doing' || m.kind === 'engaging');
    return (
      <div className="hq-feed-row" data-testid={`feed-status-${m.message_id}`} style={{
        display: 'flex', gap: 8, alignItems: 'center',
        padding: '7px 10px',
        background: 'transparent',
        borderLeft: `2px solid ${dotColor}`,
        opacity: 0.95,
      }}>
        <div style={{
          width: 22, height: 22, flexShrink: 0,
          background: `${accent}22`, color: accent,
          border: `1px solid ${accent}55`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 8, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
        }}>
          {(m.from_name || '?').slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: accent }}>
              {m.from_name}
            </span>
            <span style={{
              fontSize: 8, fontWeight: 800, padding: '1px 5px',
              background: `${dotColor}18`, color: dotColor,
              border: `1px solid ${dotColor}40`,
              letterSpacing: '0.1em', textTransform: 'uppercase',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <span style={{
                width: 4, height: 4, borderRadius: '50%', background: dotColor,
                animation: m.kind === 'done_status' || m.kind === 'alert'
                  ? 'none' : 'pulse-glow 1.2s ease-in-out infinite',
              }} />
              {label}
            </span>
            {m.to_name && m.to_name !== 'Team' && m.kind === 'engaging' && (
              <>
                <ArrowRight size={9} color="rgba(15,15,30,0.4)" />
                <span style={{ fontSize: 10, color: 'rgba(15,15,30,0.65)' }}>
                  {m.to_name}
                </span>
              </>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 9, color: 'rgba(15,15,30,0.4)', fontFamily: "'JetBrains Mono', monospace" }}>
              {fmtTime(m.created_at)}
            </span>
          </div>
          <div style={{
            fontSize: 11, lineHeight: 1.5, marginTop: 1,
            color: 'rgba(15,15,30,0.7)',
            fontStyle: 'italic',
          }}>
            {m.message}
            {isStillLive && (
              <span className="hq-typing-dots" style={{
                marginLeft: 6, color: dotColor, verticalAlign: 'middle',
              }}>
                <span /><span /><span />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isSystem) {
    return (
      <div className="hq-feed-row" data-testid={`feed-system-${m.message_id}`} style={{
        textAlign: 'center', fontSize: 10, color: 'rgba(15,15,30,0.5)',
        padding: '6px 0', letterSpacing: '0.06em',
      }}>
        — {m.message} —
      </div>
    );
  }

  return (
    <div className="hq-feed-row" data-testid={`feed-msg-${m.message_id}`} style={{
      display: 'flex', gap: 8,
      padding: 9,
      background: isAudit ? 'rgba(255,215,0,0.06)'
                  : isUser ? 'rgba(6,182,212,0.06)'
                  : 'rgba(15,15,30,0.025)',
      border: `1px solid ${isAudit ? 'rgba(255,215,0,0.3)' : isUser ? 'rgba(6,182,212,0.25)' : 'rgba(15,15,30,0.045)'}`,
    }}>
      <div style={{
        width: 26, height: 26, flexShrink: 0,
        background: `${accent}22`, color: accent,
        border: `1px solid ${accent}50`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
      }}>
        {(m.from_name || '?').slice(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: accent }}>
            {m.from_name}
          </span>
          {!isAudit && m.to_name && m.to_name !== 'Team' && (
            <>
              <ArrowRight size={9} color="rgba(15,15,30,0.4)" />
              <span style={{ fontSize: 10, color: 'rgba(15,15,30,0.65)' }}>{m.to_name}</span>
            </>
          )}
          {isAudit && (
            <span style={{
              fontSize: 8, padding: '1px 5px', fontWeight: 800,
              background: ACCENT, color: 'var(--studio-bg)',
              letterSpacing: '0.08em',
            }}>AUDIT</span>
          )}
          {isHermes && !isAudit && (
            <span style={{
              fontSize: 8, padding: '1px 5px', fontWeight: 700,
              background: 'rgba(255,215,0,0.15)', color: ACCENT,
              letterSpacing: '0.06em',
            }}>{m.kind}</span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 9, color: 'rgba(15,15,30,0.4)', fontFamily: "'JetBrains Mono', monospace" }}>
            {fmtTime(m.created_at)}
          </span>
        </div>
        <div style={{ fontSize: 12, color: 'rgba(15,15,30,0.78)', lineHeight: 1.5 }}>
          {m.message}
        </div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    const diff = (new Date() - d) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.round(diff / 60)}m`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch { return ''; }
}
